import { useEffect, useRef } from "react";
import { Report, hasReportInProgress } from "./ReportsData";

/**
 * How often the list is re-read while something is generating. Generation is a
 * background job measured in tens of seconds, so this is about how quickly the
 * row settles on screen, not about racing the backend.
 */
export const REPORTS_REFRESH_INTERVAL_MS = 5000;

export interface UseReportsAutoRefreshOptions {
  /** False while the page has no tenant, no session, or no access. */
  enabled: boolean;
  reports: Report[];
  /** Re-reads the list. Must not toggle the page's loading state. */
  onRefresh: () => void;
  intervalMs?: number;
}

/**
 * Keeps the Reports list current while any row is still being generated, and
 * stops as soon as none is — the monthly job builds reports in the background,
 * so without this the user would have to reload to find out it finished.
 *
 * Polling pauses while the tab is hidden and catches up on return: a
 * backgrounded tab would otherwise keep asking for a list nobody is reading.
 *
 * @returns whether the list is currently being polled.
 */
export function useReportsAutoRefresh({
  enabled,
  reports,
  onRefresh,
  intervalMs = REPORTS_REFRESH_INTERVAL_MS,
}: UseReportsAutoRefreshOptions): boolean {
  // Held in a ref so a new callback identity on every render does not tear
  // down and rebuild the interval.
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const isPolling = enabled && hasReportInProgress(reports);

  useEffect(() => {
    if (!isPolling) return undefined;

    let intervalId: number | undefined;

    const stop = () => {
      if (intervalId === undefined) return;
      window.clearInterval(intervalId);
      intervalId = undefined;
    };

    const start = () => {
      if (intervalId !== undefined) return;
      intervalId = window.setInterval(() => onRefreshRef.current(), intervalMs);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        stop();
        return;
      }
      // Rows may have settled while the tab was hidden — show that at once
      // instead of after a further full interval.
      onRefreshRef.current();
      start();
    };

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isPolling, intervalMs]);

  return isPolling;
}
